import './index.scss';

import React, {useContext, useEffect, useMemo, useState} from "react";
import PropTypes from 'prop-types';

import {
	VIRTUAL_ALL_PROFILE_ID,
	expandProfileIdsForSelection,
	filterLinksByProfile,
	handleAddCategory,
	generateUniqueLinkName,
	getFormattedValue,
	handleImageUpload,
	validateFormAddLink
} from '../../tools';
import ImageGallery from '../ImageGallery';
import {addLink, fetchAllStorageImages} from "../../firebaseTools";
import {useSuggestions} from "../../containers/Tools/useSuggestions";
import {StateContext} from "../../App";

const AddLink = ({categories, setLinks, setShowAddLink}) => {
	const {
		addError,
		profiles,
		profile,
		allCategories,
		setAllCategories,
		allLinks,
		setAllLinks
	} = useContext(StateContext);

	const realProfiles = useMemo(
		() => profiles.filter((item) => item.id !== VIRTUAL_ALL_PROFILE_ID),
		[profiles]
	);

	const defaultProfileIds = useMemo(() => {
		if (profile?.id && profile.id !== VIRTUAL_ALL_PROFILE_ID) return [profile.id];
		if (profile?.id === VIRTUAL_ALL_PROFILE_ID) return [VIRTUAL_ALL_PROFILE_ID];
		return realProfiles[0]?.id ? [realProfiles[0].id] : [];
	}, [profile, realProfiles]);

	const [formData, setFormData] = useState({
		name: '',
		url: '',
		category: '',
		image: null,
		profileIds: defaultProfileIds
	});

	const [imageMode, setImageMode] = useState('upload');
	const [storageImages, setStorageImages] = useState([]);
	const [loadingImages, setLoadingImages] = useState(false);

	useEffect(() => {
		const loadStorageImages = async () => {
			setLoadingImages(true);
			const images = await fetchAllStorageImages(addError);
			setStorageImages(images);
			setLoadingImages(false);
		};
		loadStorageImages();
	}, [addError]);

	const {
		suggestions,
		showSuggestions,
		updateSuggestions,
		hideSuggestions
	} = useSuggestions(categories, cat => cat.category);

	const handleAddLink = async (e) => {
		e.preventDefault();
		if (!validateFormAddLink(formData)) return;

		const category = getFormattedValue(formData.category);
		const selectedProfileIds = expandProfileIdsForSelection(formData.profileIds, profiles);
		if (selectedProfileIds.length === 0) {
			addError("At least one profile must be selected.");
			return;
		}
		const linksInScope = selectedProfileIds.reduce((acc, profileId) => [
			...acc,
			...filterLinksByProfile(allLinks, {id: profileId})
		], []);
		const name = generateUniqueLinkName(linksInScope, getFormattedValue(formData.name), category);
		const url = formData.url;

		let categoryId = null;

		categoryId = await handleAddCategory({
			allCategories,
			setAllCategories,
			categories,
			category,
			profileIds: selectedProfileIds,
			addError
		});

		if (categoryId == null) {
			addError("Failed to add category");
			setShowAddLink(false);
			return;
		}

		const imageUrl = await handleImageUpload(formData.image, addError);
		if (!imageUrl || imageUrl.empty) {
			addError("Failed to upload image");
			return;
		}

		const newLink = {
			categoryId,
			category,
			name,
			url,
			image: imageUrl,
			profileIds: selectedProfileIds
		};
		const response = await addLink(newLink, addError);
		if (response?.id) {
			const withId = {...newLink, id: response.id};
			setAllLinks((prevLinks) => [...prevLinks, withId]);
			setLinks(prevLinks => [...prevLinks, withId]);
			setShowAddLink(false);
		}
	};

	const handleChange = (e) => {
		const {name, value} = e.target;
		setFormData(prev => ({
			...prev, [name]: value
		}));

		if (name === 'category') {
			updateSuggestions(value);
		}
	};

	const handleSuggestionClick = (suggestion) => {
		setFormData(prev => ({...prev, category: suggestion}));
		hideSuggestions();
	};

	return (
		<div className="addLink-container">
			<div className="addLink-form">
				<button className="close-button" onClick={() => setShowAddLink(false)}>×</button>
				<h2>Add New Link</h2>
				<form onSubmit={handleAddLink}>
					<div className="form-group category-input-container">
						<input
							type="text"
							name="category"
							value={formData.category}
							onChange={handleChange}
							placeholder="Category"
							required
							autoComplete="off"
						/>
						{showSuggestions && (
							<ul className="category-suggestions">
								{suggestions.map((suggestion, index) => (
									<li key={`${suggestion}-${index}`}
									    onClick={() => handleSuggestionClick(suggestion)}>
										{suggestion}
									</li>
								))}
							</ul>
						)}
					</div>
					<p>
						<input
							type="text"
							name="name"
							placeholder="Link Name"
							value={formData.name}
							onChange={handleChange}
							required
						/>
					</p>
					<p>
						<input
							type="url"
							name="url"
							placeholder="URL"
							value={formData.url}
							onChange={handleChange}
							required
						/>
					</p>
					<p>
						<select
							className="profiles-select"
							value={formData.profileIds[0] || ''}
							onChange={(event) => {
								setFormData((prev) => ({...prev, profileIds: [event.target.value]}));
							}}
						>
							{profiles.map((item) => (
								<option key={item.id} value={item.id}>
									{item.profileName}
								</option>
							))}
						</select>
					</p>
					<div className="image-selection">
						<div className="image-selection__tabs">
							<button
								type="button"
								className={`image-selection__tab ${imageMode === 'upload' ? 'image-selection__tab--active' : ''}`}
								onClick={() => {
									setImageMode('upload');
									setFormData(prev => ({...prev, image: null}));
								}}
							>
								Upload New
							</button>
							<button
								type="button"
								className={`image-selection__tab ${imageMode === 'select' ? 'image-selection__tab--active' : ''}`}
								onClick={() => {
									setImageMode('select');
									setFormData(prev => ({...prev, image: null}));
								}}
								disabled={loadingImages}
							>
								{loadingImages ? 'Loading...' : 'Select Existing'}
							</button>
						</div>
						{imageMode === 'upload' ? (
							<input
								type="file"
								name="image"
								placeholder="Image"
								onChange={(e) => setFormData(prev => ({
									...prev,
									image: e.target.files[0]
								}))}
							/>
						) : (
							<ImageGallery
								images={storageImages}
								selectedImage={typeof formData.image === 'string' ? formData.image : null}
								onSelect={(url) => setFormData(prev => ({...prev, image: url}))}
							/>
						)}
					</div>
					<button type="submit">Add Link</button>
				</form>
			</div>
		</div>
	);
};

AddLink.propTypes = {
	categories: PropTypes.array.isRequired,
	setLinks: PropTypes.func.isRequired,
	setShowAddLink: PropTypes.func.isRequired,
};

export default AddLink;
